import { useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useToast } from './useToast';
import { useConfirm } from './useConfirm';
import type { Invoice, InvoiceStatus } from '../types';
import { callExtractInvoicePdfBase64 } from '../lib/catalogFeedingService';
import { getToday } from '../utils/dateUtils';
import * as XLSX from 'xlsx';
import {
  getEffectiveStatus,
  STATUS_CONFIG,
  fileToBase64,
  EMPTY_LINE,
  type FormLine,
} from './useInvoicingData';

// ─── PARAMS ──────────────────────────────────────────────────────────────

interface UseInvoicingActionsParams {
  // auth
  userId: string | undefined;
  // data
  projects: { id: string; code: string; name: string; is_active: boolean }[];
  filtered: Invoice[];
  // form state
  formClientName: string;
  setFormClientName: (v: string) => void;
  formClientAddress: string;
  setFormClientAddress: (v: string) => void;
  formClientEmail: string;
  setFormClientEmail: (v: string) => void;
  formProjectId: string;
  setFormProjectId: (v: string) => void;
  formInvoiceDate: string;
  setFormInvoiceDate: (v: string) => void;
  formDueDate: string;
  setFormDueDate: (v: string) => void;
  formTvaRate: number;
  setFormTvaRate: (v: number) => void;
  formNotes: string;
  setFormNotes: (v: string) => void;
  formLines: FormLine[];
  setFormLines: React.Dispatch<React.SetStateAction<FormLine[]>>;
  editingId: string | null;
  setEditingId: (v: string | null) => void;
  saving: boolean;
  setSaving: (v: boolean) => void;
  showForm: boolean;
  setShowForm: (v: boolean) => void;
  // expand
  setExpandedId: React.Dispatch<React.SetStateAction<string | null>>;
  // pdf
  pdfFile: File | null;
  setPdfFile: (v: File | null) => void;
  setDragOver: (v: boolean) => void;
  setExtracting: (v: boolean) => void;
  setExtractionError: (v: string | null) => void;
  signatureDetected: boolean;
  setSignatureDetected: (v: boolean) => void;
  setValidationMention: (v: string | null) => void;
  // helpers
  resetForm: () => void;
  computeDefaultDueDate: (invoiceDate: string) => string;
  fetchData: () => Promise<void>;
  // filters
  dateStart: string;
  dateEnd: string;
}

// ─── HOOK ────────────────────────────────────────────────────────────────

export function useInvoicingActions(params: UseInvoicingActionsParams) {
  const { toast } = useToast();
  const { confirm: confirmDialog } = useConfirm();
  const {
    userId,
    filtered,
    formClientName, setFormClientName,
    formClientAddress, setFormClientAddress,
    formClientEmail, setFormClientEmail,
    formProjectId, setFormProjectId,
    formInvoiceDate, setFormInvoiceDate,
    formDueDate, setFormDueDate,
    formTvaRate, setFormTvaRate,
    formNotes, setFormNotes,
    formLines, setFormLines,
    editingId, setEditingId,
    setSaving,
    setShowForm,
    setExpandedId,
    pdfFile, setPdfFile,
    setDragOver,
    setExtracting,
    setExtractionError,
    signatureDetected, setSignatureDetected,
    setValidationMention,
    resetForm, computeDefaultDueDate,
    fetchData,
    dateStart, dateEnd,
  } = params;

  // ─── PDF DROP & OCR EXTRACTION ─────────────────────────────────────────

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type === 'application/pdf' || f.name.endsWith('.pdf'));
    if (files.length > 0) {
      setPdfFile(files[0]);
      setExtractionError(null);
      handleExtract(files[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setPdfFile(files[0]);
      setExtractionError(null);
      handleExtract(files[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExtract = async (file: File) => {
    setExtracting(true);
    setExtractionError(null);
    try {
      const pdfBase64 = await fileToBase64(file);
      const result = await callExtractInvoicePdfBase64(pdfBase64);

      if (!result?.success || !result.extracted) {
        setExtractionError(result?.error || 'Extraction échouée — vérifiez la configuration.');
        setExtracting(false);
        return;
      }

      // Auto-remplir le formulaire
      const ext = result.extracted;

      // Matcher le projet si code extrait
      let matchedProjectId = '';
      if (ext.project_code) {
        const codeRaw = ext.project_code.trim().toUpperCase();
        const { data: matchedProject } = await supabase
          .from('projects')
          .select('id, code, name')
          .ilike('code', codeRaw)
          .eq('is_active', true)
          .limit(1)
          .maybeSingle();
        if (matchedProject) {
          matchedProjectId = matchedProject.id;
        }
      }

      setFormClientName(ext.client_name || '');
      setFormClientAddress(ext.client_address || '');
      setFormClientEmail(ext.client_email || '');
      setFormProjectId(matchedProjectId);
      setFormInvoiceDate(ext.invoice_date || getToday());
      setFormDueDate(ext.due_date || '');
      setFormTvaRate(ext.tva_rate || 20);
      setFormNotes(ext.notes || '');
      setFormLines(
        ext.lines.length > 0
          ? ext.lines.map((l: { description: string; quantity: number; unit?: string; unit_price: number }) => ({
              description: l.description,
              quantity: l.quantity,
              unit: (l.unit || 'u').toLowerCase(),
              unit_price: l.unit_price,
            }))
          : [{ ...EMPTY_LINE }]
      );
      setSignatureDetected(ext.has_signature ?? false);
      setValidationMention(ext.validation_mention ?? null);
      setShowForm(true);
      setEditingId(null);
    } catch (err) {
      console.error('[AdminInvoicing] handleExtract error:', err);
      setExtractionError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setExtracting(false);
    }
  };

  // ─── CREATE ────────────────────────────────────────────────────────────

  const handleCreate = useCallback(async () => {
    if (!formClientName.trim()) { toast.warning('Le nom du client est obligatoire.'); return; }
    if (formLines.every(l => !l.description.trim())) { toast.warning('Au moins une ligne est requise.'); return; }

    setSaving(true);
    try {
      // 1. Générer le numéro de facture (fallback local si la RPC n'existe pas)
      let invoiceNumber: string;
      const { data: numData, error: numErr } = await supabase.rpc('generate_invoice_number');
      if (numErr || !numData) {
        console.warn('[AdminInvoicing] RPC generate_invoice_number indisponible, fallback local:', numErr?.message);
        const y = new Date().getFullYear();
        const ts = Date.now().toString(36).toUpperCase();
        invoiceNumber = `FA-${y}-${ts}`;
      } else {
        invoiceNumber = numData as string;
      }

      const dueDate = formDueDate || computeDefaultDueDate(formInvoiceDate);

      // 2. Chercher l'employee_id de l'utilisateur connecté
      let createdBy: string | null = null;
      if (userId) {
        const { data: emp } = await supabase
          .from('employees')
          .select('id')
          .eq('auth_user_id', userId)
          .single();
        if (emp) createdBy = emp.id;
      }

      // 3. Insérer la facture
      const { data: inv, error: invErr } = await supabase
        .from('invoices')
        .insert({
          invoice_number: invoiceNumber,
          project_id: formProjectId || null,
          client_name: formClientName.trim(),
          client_address: formClientAddress.trim(),
          client_email: formClientEmail.trim() || null,
          invoice_date: formInvoiceDate,
          due_date: dueDate,
          tva_rate: formTvaRate,
          notes: formNotes.trim() || null,
          created_by: createdBy,
          status: signatureDetected ? 'sent' : 'draft',
        })
        .select()
        .single();

      if (invErr || !inv) {
        console.error('[AdminInvoicing] Insert invoices error:', invErr);
        throw new Error(invErr?.message || 'Impossible de créer la facture. Vérifiez que la table invoices existe.');
      }

      // 4. Insérer les lignes
      const validLines = formLines.filter(l => l.description.trim());
      if (validLines.length > 0) {
        const linesToInsert = validLines.map((l, i) => ({
          invoice_id: inv.id,
          description: l.description.trim(),
          quantity: l.quantity,
          unit: l.unit,
          unit_price: l.unit_price,
          sort_order: i,
        }));
        const { error: linesErr } = await supabase.from('invoice_lines').insert(linesToInsert);
        if (linesErr) throw linesErr;
      }

      // 5. Upload du PDF source si présent
      if (pdfFile) {
        try {
          const fileName = `${invoiceNumber}_source.pdf`;
          const { error: uploadErr } = await supabase.storage
            .from('invoice-pdfs')
            .upload(fileName, pdfFile, { contentType: 'application/pdf', upsert: true });
          if (!uploadErr) {
            const { data: urlData } = supabase.storage.from('invoice-pdfs').getPublicUrl(fileName);
            if (urlData?.publicUrl) {
              await supabase.from('invoices').update({ pdf_url: urlData.publicUrl }).eq('id', inv.id);
            }
          }
        } catch (storageErr) {
          console.warn('[AdminInvoicing] Storage upload failed:', storageErr);
        }
      }

      toast.success('Facture créée.');
      resetForm();
      await fetchData();
    } catch (err: unknown) {
      console.error('[AdminInvoicing] handleCreate error:', err);
      const msg = err instanceof Error ? err.message : 'Erreur inconnue';
      toast.error(`Erreur : ${msg}`);
    } finally {
      setSaving(false);
    }
  }, [
    formClientName, formLines, formDueDate, formInvoiceDate, formProjectId,
    formClientAddress, formClientEmail, formTvaRate, formNotes,
    signatureDetected, pdfFile, userId,
    setSaving, computeDefaultDueDate, resetForm, fetchData,
  ]);

  // ─── EDIT ──────────────────────────────────────────────────────────────

  const startEdit = useCallback(async (inv: Invoice) => {
    setEditingId(inv.id);
    setFormClientName(inv.client_name);
    setFormClientAddress(inv.client_address || '');
    setFormClientEmail(inv.client_email || '');
    setFormProjectId(inv.project_id || '');
    setFormInvoiceDate(inv.invoice_date);
    setFormDueDate(inv.due_date);
    setFormTvaRate(inv.tva_rate);
    setFormNotes(inv.notes || '');
    setShowForm(true);

    const { data } = await supabase
      .from('invoice_lines')
      .select('*')
      .eq('invoice_id', inv.id)
      .order('sort_order');

    if (data && data.length > 0) {
      setFormLines(data.map(l => ({
        id: l.id,
        description: l.description,
        quantity: l.quantity,
        unit: l.unit,
        unit_price: l.unit_price,
      })));
    } else {
      setFormLines([{ ...EMPTY_LINE }]);
    }

    setExpandedId(null);
  }, [
    setEditingId, setFormClientName, setFormClientAddress, setFormClientEmail,
    setFormProjectId, setFormInvoiceDate, setFormDueDate, setFormTvaRate,
    setFormNotes, setShowForm, setFormLines, setExpandedId,
  ]);

  const saveEdit = useCallback(async () => {
    if (!editingId) return;
    if (!formClientName.trim()) { toast.warning('Le nom du client est obligatoire.'); return; }

    setSaving(true);
    try {
      const dueDate = formDueDate || computeDefaultDueDate(formInvoiceDate);

      const { error: updErr } = await supabase
        .from('invoices')
        .update({
          project_id: formProjectId || null,
          client_name: formClientName.trim(),
          client_address: formClientAddress.trim(),
          client_email: formClientEmail.trim() || null,
          invoice_date: formInvoiceDate,
          due_date: dueDate,
          tva_rate: formTvaRate,
          notes: formNotes.trim() || null,
        })
        .eq('id', editingId);
      if (updErr) throw updErr;

      await supabase.from('invoice_lines').delete().eq('invoice_id', editingId);

      const validLines = formLines.filter(l => l.description.trim());
      if (validLines.length > 0) {
        const linesToInsert = validLines.map((l, i) => ({
          invoice_id: editingId,
          description: l.description.trim(),
          quantity: l.quantity,
          unit: l.unit,
          unit_price: l.unit_price,
          sort_order: i,
        }));
        const { error: linesErr } = await supabase.from('invoice_lines').insert(linesToInsert);
        if (linesErr) throw linesErr;
      }

      toast.success('Facture modifiée.');
      resetForm();
      await fetchData();
    } catch (err: unknown) {
      console.error('[AdminInvoicing] saveEdit error:', err);
      const msg = err instanceof Error ? err.message : 'Erreur inconnue';
      toast.error(`Erreur : ${msg}`);
    } finally {
      setSaving(false);
    }
  }, [
    editingId, formClientName, formDueDate, formInvoiceDate, formProjectId,
    formClientAddress, formClientEmail, formTvaRate, formNotes, formLines,
    setSaving, computeDefaultDueDate, resetForm, fetchData,
  ]);

  // ─── DELETE (brouillons uniquement) ────────────────────────────────────

  const handleDelete = useCallback(async (id: string) => {
    const ok = await confirmDialog({ title: 'Supprimer', message: 'Supprimer ce brouillon ?', variant: 'danger' });
    if (!ok) return;
    try {
      const { error: linesErr } = await supabase.from('invoice_lines').delete().eq('invoice_id', id);
      if (linesErr) throw linesErr;
      const { error: invErr } = await supabase.from('invoices').delete().eq('id', id);
      if (invErr) throw invErr;
      toast.success('Facture supprimée.');
      await fetchData();
    } catch (err) {
      console.error('[AdminInvoicing] handleDelete error:', err);
      const msg = err instanceof Error ? err.message : 'Erreur inconnue';
      toast.error(`Erreur : ${msg}`);
    }
  }, [fetchData, confirmDialog]);

  // ─── STATUS TRANSITIONS ────────────────────────────────────────────────

  const updateStatus = useCallback(async (id: string, newStatus: InvoiceStatus) => {
    try {
      const { error } = await supabase
        .from('invoices')
        .update({ status: newStatus })
        .eq('id', id);
      if (error) throw error;
      toast.success('Statut mis à jour.');
      await fetchData();
    } catch (err) {
      console.error('[AdminInvoicing] updateStatus error:', err);
      const msg = err instanceof Error ? err.message : 'Erreur inconnue';
      toast.error(`Erreur : ${msg}`);
    }
  }, [fetchData]);

  // ─── EXPORT EXCEL ──────────────────────────────────────────────────────

  const exportExcel = useCallback(() => {
    const rows = filtered.map(inv => ({
      'N° Facture': inv.invoice_number,
      'Date': inv.invoice_date,
      'Client': inv.client_name,
      'Adresse': inv.client_address || '',
      'Email': inv.client_email || '',
      'Affaire': inv.project ? `${inv.project.code} - ${inv.project.name}` : 'Libre',
      'Total HT (€)': inv.total_ht,
      'TVA (%)': inv.tva_rate,
      'Total TTC (€)': inv.total_ttc,
      'Statut': STATUS_CONFIG[getEffectiveStatus(inv)]?.label || inv.status,
      'Échéance': inv.due_date,
      'Notes': inv.notes || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Factures');
    XLSX.writeFile(wb, `factures_${dateStart}_${dateEnd}.xlsx`);
  }, [filtered, dateStart, dateEnd]);

  return {
    handleFileDrop,
    handleFileSelect,
    handleExtract,
    handleCreate,
    startEdit,
    saveEdit,
    handleDelete,
    updateStatus,
    exportExcel,
  };
}
